import { FieldPath, FieldValues, useFormContext } from 'react-hook-form';
import { cn } from '../../utils/cn';
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '../form/form-primitives';
import { Stack } from '../stack/stack';

type FormTextFieldProps<TFieldValues extends FieldValues> = {
  label: string;
  inputName: FieldPath<TFieldValues>;
  placeholder?: string;
  isOptional?: boolean;
  isDisabled?: boolean;
  disablePasswordManager?: boolean;
};

export const FormTextField = <TFieldValues extends FieldValues>({
  label,
  inputName,
  placeholder,
  isOptional = false,
  isDisabled = false,
  disablePasswordManager = false,
}: FormTextFieldProps<TFieldValues>) => {
  const { control } = useFormContext();

  return (
    <FormField
      control={control}
      name={inputName}
      render={({ field, formState }) => {
        return (
          <Stack space={1} align="stretch">
            <FormItem
              className={cn(
                'rounded-lg border border-seapoint-gray-border bg-white px-4 py-3 focus-within:outline-none focus-within:ring-2 focus-within:ring-seapoint-yellow focus-within:ring-offset-2 has-[:disabled]:cursor-not-allowed has-[:disabled]:bg-seapoint-gray-background-modal',
                { 'border-seapoint-red': !!formState.errors[inputName] },
              )}
            >
              <Stack space={1} align="stretch">
                <FormLabel className="text-xs font-medium text-seapoint-gray">
                  {label}
                  {isOptional && <span className="text-inherit"> (Optional)</span>}
                </FormLabel>

                <FormControl>
                  <input
                    type="text"
                    placeholder={placeholder}
                    className="font-base w-full font-medium text-black placeholder:text-seapoint-gray focus-visible:outline-none disabled:cursor-not-allowed disabled:bg-seapoint-gray-background-modal disabled:placeholder:text-seapoint-gray/80"
                    aria-required={!isOptional}
                    {...field}
                    // Prevents controlled / uncontrolled console warning message
                    value={field.value ?? ''}
                    disabled={isDisabled || field.disabled}
                    data-1p-ignore={disablePasswordManager}
                  />
                </FormControl>
              </Stack>
            </FormItem>

            <FormMessage className="w-full text-right" />
          </Stack>
        );
      }}
    />
  );
};
